import { FISHYDEX_CATALOG_VIEWS } from "./surface-projection.js";
import {
  fishydexBrowserStorage,
  removeAndVerify,
  verifyStoredValue,
} from "./browser-storage-provider.js";

const SCHEMA = "fishystuff.fishydex-device-ui.v4";
const SLOT = "__fishystuffFishydexDeviceUiV4";
const LEGACY_UI_KEY = "fishystuff.fishydex.ui.v1";
const MAX_SEARCH_QUERY_LENGTH = 160;
const MAX_DEVICE_UI_RAW_CHARS = 8_192;
const MAX_OPERATION_ID_CHARS = 160;
const FIELDS = Object.freeze([
  "search_query",
  "show_non_fish",
  "sort_field",
  "sort_direction",
  "catalog_view",
]);

export const FISHYDEX_DEVICE_UI_KEY = "fishystuff.fishydex.device-ui.v4";
export const FISHYDEX_DEVICE_UI_BRAND = Symbol.for("fishystuff.fishydex-device-ui.v4");

function clone(value) {
  return value === undefined ? undefined : structuredClone(value);
}

function exactRecord(value, fields, label) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new TypeError(`${label} must be an object`);
  }
  const actual = Object.keys(value).sort();
  const expected = fields.slice().sort();
  if (actual.length !== expected.length
      || actual.some((field, index) => field !== expected[index])) {
    throw new TypeError(`${label} has unsupported or missing fields`);
  }
  return value;
}

function exactOptions(value, fields, label) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new TypeError(`${label} must be an object`);
  }
  const allowed = new Set(fields);
  if (Object.keys(value).some((field) => !allowed.has(field))) {
    throw new TypeError(`${label} has unsupported fields`);
  }
  return value;
}

function expectedRevision(value) {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new TypeError(
      "Fishydex device-UI expected revision must be a non-negative safe integer",
    );
  }
  return value;
}

function operationId(value) {
  if (value !== undefined) {
    if (
      typeof value !== "string"
      || !value
      || value !== value.trim()
      || value.length > MAX_OPERATION_ID_CHARS
      || /[\u0000-\u001f\u007f]/.test(value)
    ) {
      throw new TypeError("Fishydex device-UI operation ID must be bounded canonical text");
    }
    return value;
  }
  const uuid = globalThis.crypto?.randomUUID?.();
  return uuid
    ? `fishydex-device-ui.${uuid}`
    : `fishydex-device-ui.${Date.now().toString(36)}.${Math.random().toString(36).slice(2)}`;
}

function replaceOptions(value) {
  const parsed = exactOptions(
    value,
    ["expectedRevision", "operationId"],
    "Fishydex device-UI replace options",
  );
  return {
    expectedRevision: parsed.expectedRevision === undefined
      ? undefined
      : expectedRevision(parsed.expectedRevision),
    operationId: operationId(parsed.operationId),
  };
}

function commandResult(value, command, fields = {}) {
  return Object.freeze({ ...value, ...fields, operationId: command.operationId });
}

function requireStorage(storage) {
  for (const method of ["getItem", "setItem", "removeItem"]) {
    if (typeof storage?.[method] !== "function") {
      throw new TypeError(`Fishydex device-UI storage is missing ${method}()`);
    }
  }
  return storage;
}

export function defaultFishydexDeviceUiPreferences() {
  return {
    search_query: "",
    show_non_fish: false,
    sort_field: "price",
    sort_direction: "desc",
    catalog_view: "grade",
  };
}

export function decodeFishydexDeviceUiPreferences(value) {
  const input = exactRecord(value, FIELDS, "Fishydex device UI preferences");
  if (typeof input.search_query !== "string") {
    throw new TypeError("Fishydex search query must be a string");
  }
  if (input.search_query !== input.search_query.trim()) {
    throw new TypeError("Fishydex search query must be trimmed");
  }
  if (
    Array.from(input.search_query).length > MAX_SEARCH_QUERY_LENGTH
    || /[\u0000-\u001f\u007f]/.test(input.search_query)
  ) {
    throw new TypeError("Fishydex search query exceeds its bounded text contract");
  }
  if (typeof input.show_non_fish !== "boolean") {
    throw new TypeError("Fishydex non-fish inclusion must be a boolean");
  }
  if (!["name", "price"].includes(input.sort_field)) {
    throw new TypeError("Fishydex sort field is invalid");
  }
  if (!["asc", "desc"].includes(input.sort_direction)) {
    throw new TypeError("Fishydex sort direction is invalid");
  }
  if (!FISHYDEX_CATALOG_VIEWS.includes(input.catalog_view)) {
    throw new TypeError("Fishydex catalog view is invalid");
  }
  return {
    search_query: input.search_query,
    show_non_fish: input.show_non_fish,
    sort_field: input.sort_field,
    sort_direction: input.sort_direction,
    catalog_view: input.catalog_view,
  };
}

function projection(state) {
  const document = state.document ?? state.authoritative?.document ?? null;
  return Object.freeze({
    kind: state.kind,
    revision: document?.revision ?? (state.kind === "missing" ? 0 : null),
    preferences: document ? clone(document.preferences) : null,
    raw: state.raw ?? null,
    legacyRaw: state.legacyRaw ?? null,
    reason: state.reason ?? null,
    conflict: state.conflict ?? null,
    authoritative: state.authoritative ? projection(state.authoritative) : null,
  });
}

export function createFishydexDeviceUiRepository({
  storage,
  eventTarget = globalThis.window,
} = {}) {
  const provider = requireStorage(fishydexBrowserStorage(storage));
  const listeners = new Set();
  let state = { kind: "uninitialized" };
  let loaded = false;
  let disposed = false;

  function alive() {
    if (disposed) {
      throw new Error("Fishydex device-UI repository is disposed");
    }
  }

  function decode(raw) {
    if (raw === null) {
      return { kind: "missing", raw: null };
    }
    if (typeof raw !== "string") {
      return { kind: "invalid", raw, reason: "stored value must be text" };
    }
    if (raw === "") {
      return { kind: "invalid", raw };
    }
    if (raw.length > MAX_DEVICE_UI_RAW_CHARS) {
      return {
        kind: "invalid",
        raw,
        reason: "Fishydex device UI document exceeds its v4 raw-size limit",
      };
    }
    let value;
    try {
      value = JSON.parse(raw);
    } catch (_error) {
      return { kind: "invalid", raw };
    }
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      return { kind: "invalid", raw };
    }
    if (value.schema !== SCHEMA) {
      return { kind: "unsupportedVersion", raw };
    }
    try {
      const document = exactRecord(
        value,
        ["schema", "revision", "preferences"],
        "Fishydex device UI document",
      );
      if (!Number.isSafeInteger(document.revision) || document.revision < 1) {
        throw new TypeError("Fishydex device UI revision is invalid");
      }
      return {
        kind: "ready",
        raw,
        document: {
          schema: SCHEMA,
          revision: document.revision,
          preferences: decodeFishydexDeviceUiPreferences(document.preferences),
        },
      };
    } catch (error) {
      return { kind: "invalid", raw, reason: String(error?.message || error) };
    }
  }

  function readRaw(key) {
    return provider.getItem(key);
  }

  function read() {
    alive();
    try { state = decode(readRaw(FISHYDEX_DEVICE_UI_KEY)); }
    catch (error) { state = { kind: "unavailable", reason: String(error?.message || error) }; }
    loaded = true;
    return projection(state);
  }

  function publish() {
    const next = projection(state);
    for (const listener of listeners) {
      listener(next);
    }
    return next;
  }

  async function replace(preferencesValue, options = {}) {
    alive();
    const command = replaceOptions(options);
    const preferences = decodeFishydexDeviceUiPreferences(preferencesValue);
    const current = read();
    if (current.kind !== "missing" && current.kind !== "ready") {
      return commandResult(current, command);
    }
    if (state.kind === "ready"
        && JSON.stringify(state.document.preferences) === JSON.stringify(preferences)) {
      return commandResult(projection(state), command);
    }
    if (
      command.expectedRevision !== undefined
      && command.expectedRevision !== current.revision
    ) {
      state = {
        kind: "conflict",
        conflict: "revision",
        authoritative: state,
      };
      return commandResult(publish(), command, {
        expectedRevision: command.expectedRevision,
        actualRevision: current.revision,
      });
    }
    const document = {
      schema: SCHEMA,
      revision: current.revision + 1,
      preferences,
    };
    const authoritative = state;
    const encoded = JSON.stringify(document);
    if (encoded.length > MAX_DEVICE_UI_RAW_CHARS) {
      throw new TypeError("Fishydex device UI document exceeds its v4 raw-size limit");
    }
    try {
      const before = authoritative.raw ?? null;
      if (provider.getItem(FISHYDEX_DEVICE_UI_KEY) !== before) {
        read();
        const changed = state;
        state = {
          kind: "conflict",
          conflict: "revision",
          reason: "Fishydex device UI changed before acknowledgement",
          authoritative: changed,
        };
        return commandResult(publish(), command, {
          expectedRevision: current.revision,
          actualRevision: projection(changed).revision,
        });
      }
      provider.setItem(FISHYDEX_DEVICE_UI_KEY, encoded);
      verifyStoredValue(provider, FISHYDEX_DEVICE_UI_KEY, encoded);
      state = { kind: "ready", document, raw: encoded };
    } catch (error) {
      state = {
        kind: "unavailable",
        reason: String(error?.message || error),
        authoritative,
      };
    }
    return commandResult(publish(), command);
  }

  async function discardLegacy() {
    alive();
    try {
      removeAndVerify(provider, LEGACY_UI_KEY);
      return Object.freeze({ kind: "missing" });
    } catch (error) {
      return Object.freeze({ kind: "unavailable", reason: String(error?.message || error) });
    }
  }

  async function reset() {
    alive();
    const legacy = await discardLegacy();
    if (legacy.kind !== "missing") {
      return legacy;
    }
    try {
      removeAndVerify(provider, FISHYDEX_DEVICE_UI_KEY);
      state = { kind: "missing" };
    } catch (error) {
      state = { kind: "unavailable", reason: String(error?.message || error) };
    }
    loaded = true;
    return publish();
  }

  function exportRaw() {
    alive();
    try {
      const raw = provider.getItem(FISHYDEX_DEVICE_UI_KEY);
      const legacyRaw = provider.getItem(LEGACY_UI_KEY);
      return Object.freeze({
        kind: raw === null && legacyRaw === null ? "missing" : "ready",
        records: Object.freeze([
          Object.freeze({ key: FISHYDEX_DEVICE_UI_KEY, raw }),
          Object.freeze({ key: LEGACY_UI_KEY, raw: legacyRaw }),
        ]),
      });
    } catch (error) {
      return Object.freeze({
        kind: "unavailable",
        records: Object.freeze([]),
        reason: String(error?.message || error),
      });
    }
  }

  function refresh() {
    if (!loaded || disposed) {
      return;
    }
    read();
    publish();
  }

  function onStorage(event) {
    if (event?.storageArea && event.storageArea !== provider) {
      return;
    }
    if (event?.key === null || event?.key === FISHYDEX_DEVICE_UI_KEY) {
      refresh();
    }
  }

  function onPageShow(event) {
    if (event?.persisted === true) {
      refresh();
    }
  }

  eventTarget?.addEventListener?.("storage", onStorage);
  eventTarget?.addEventListener?.("pageshow", onPageShow);

  return Object.freeze({
    [FISHYDEX_DEVICE_UI_BRAND]: 1,
    load() {
      alive();
      return loaded ? projection(state) : read();
    },
    state() {
      alive();
      return projection(state);
    },
    replace,
    reset,
    discardLegacy,
    exportRaw,
    subscribe(listener) {
      alive();
      if (typeof listener !== "function") {
        throw new TypeError("Fishydex device-UI subscriber must be a function");
      }
      listeners.add(listener);
      if (loaded) {
        listener(projection(state));
      }
      return () => listeners.delete(listener);
    },
    dispose() {
      if (disposed) {
        return;
      }
      disposed = true;
      listeners.clear();
      eventTarget?.removeEventListener?.("storage", onStorage);
      eventTarget?.removeEventListener?.("pageshow", onPageShow);
    },
  });
}

export function browserFishydexDeviceUiRepository(options = {}) {
  const windowRef = options.window ?? globalThis.window;
  const occupied = windowRef?.[SLOT];
  if (occupied !== undefined) {
    if (occupied?.[FISHYDEX_DEVICE_UI_BRAND] !== 1) {
      throw new TypeError("Shared Fishydex device-UI repository is incompatible");
    }
    return occupied;
  }
  const repository = createFishydexDeviceUiRepository({
    storage: fishydexBrowserStorage(options.storage),
    eventTarget: options.eventTarget ?? windowRef,
  });
  if (windowRef && typeof windowRef === "object") {
    windowRef[SLOT] = repository;
  }
  return repository;
}
